import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { intl, fetchData, HTTP_STATUS, DmsModal, DmsButton } from "dmsbasereact";
import { Box, Typography, CircularProgress } from "@mui/material";
import { ViewModeContent } from "./ViewModeContent";

export const InvoiceDocumentDetailModal = ({
  open,
  onClose,
  invoice = null,
  createdByLabel = undefined,
}) => {
  const [loading, setLoading] = useState(false);
  const [enrichedInvoice, setEnrichedInvoice] = useState(null);

  const loadAdditionalData = useCallback(async (invoiceData) => {
    if (!invoiceData) return null;
    try {
      const response = await fetchData(
        `/invoice_document/${invoiceData.pk}`,
        "GET",
        true
      );
      if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
        // The single-record GET endpoint doesn't run the table's
        // _populate_created_by_display(), so created_by_display lands as
        // null in `data`. Carry it over from the table row that triggered
        // the modal — that row already has the resolved label.
        return {
          ...response.data.data,
          created_by_display:
            response.data.data.created_by_display ??
            invoiceData.created_by_display ??
            null,
        };
      }
    } catch (error) {
      console.error("Error loading invoice details:", error);
    }
    return invoiceData;
  }, []);

  useEffect(() => {
    if (open && invoice) {
      setLoading(true);
      loadAdditionalData(invoice).then((enriched) => {
        setEnrichedInvoice(enriched);
        setLoading(false);
      });
    } else if (!open) {
      setEnrichedInvoice(null);
    }
  }, [open, invoice, loadAdditionalData]);

  const danfe = enrichedInvoice?.danfe_number || invoice?.danfe_number || '';

  return (
    <DmsModal
      isVisible={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <Box>
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 700 }}>
            {`${intl.get("InvoiceDocumentDetails").d("Detalhes da Nota Fiscal")}${danfe ? `: ${danfe}` : ''}`}
          </Typography>
        </Box>
        <Box sx={{ borderBottom: '3px solid #1565c0', mx: -3 }} />
      </Box>
      <DmsModal.Body>
        <Box sx={{ p: 2.5 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : enrichedInvoice ? (
            <ViewModeContent data={enrichedInvoice} createdByLabel={createdByLabel} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              {intl.get("NoDataAvailable").d("No data available")}
            </Typography>
          )}
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, width: '100%' }}>
          <DmsButton
            text={intl.get("Close").d("Close")}
            onClick={onClose}
          />
        </Box>
      </DmsModal.Footer>
    </DmsModal>
  );
};

InvoiceDocumentDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoice: PropTypes.object,
  createdByLabel: PropTypes.string,
};
